import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { C, CHART_COLORS } from '../utils/theme.js';
import Card from './Card.jsx';
import StatBox from './StatBox.jsx';
import { t } from '../utils/i18n.js';
import { gridToLatLon, haversine } from '../utils/gridLocator.js';
import { lookupDXCC } from '../utils/dxccLookup.js';
import { addDarkBasemap } from '../utils/mapTiles.js';

var API = window.location.hostname === "localhost" ? "http://localhost:3001" : "";

// PSK Reporter / QRZ default band palette (recognizable band-by-colour).
var BAND_COLORS = {
  "160m": "#7cfc00", "80m": "#e550e5", "60m": "#00008b", "40m": "#5959ff",
  "30m": "#62d962", "20m": "#f2c40c", "17m": "#f2f261", "15m": "#cca166",
  "12m": "#b22222", "10m": "#ff69b4", "6m": "#ff0000", "2m": "#ff1493", "70cm": "#999900"
};

var BAND_ORDER = ["160m","80m","60m","40m","30m","20m","17m","15m","12m","10m","6m","2m"];

// Band propagation characteristics for time-of-day scoring
var BAND_NIGHT_BIAS = { "160m": 0.9, "80m": 0.7, "60m": 0.5, "40m": 0.3, "30m": 0.1, "20m": -0.2, "17m": -0.4, "15m": -0.6, "12m": -0.7, "10m": -0.8, "6m": -0.5, "2m": 0 };

export default function RealtimeView(props) {
  var lang = props.lang;
  var myGrid = props.myGrid;
  var userQsos = props.qsos || [];

  // Pre-compute sets for "New One" detection
  var newOneData = useMemo(function() {
    var entities = new Set();
    var bandEntity = new Set();
    var grids4 = new Set();
    for (var i = 0; i < userQsos.length; i++) {
      var q = userQsos[i];
      if (q.entity && q.entity !== "Unknown") entities.add(q.entity);
      if (q.entity && q.band) bandEntity.add(q.band + "|" + q.entity);
      if (q.grid) grids4.add(q.grid.substring(0, 4));
    }
    return { entities: entities, bandEntity: bandEntity, grids: grids4 };
  }, [userQsos]);

  var showNewOnlyState = useState(false);
  var showNewOnly = showNewOnlyState[0]; var setShowNewOnly = showNewOnlyState[1];

  var spotsState = useState([]);
  var spots = spotsState[0]; var setSpots = spotsState[1];
  var summaryState = useState([]);
  var summary = summaryState[0]; var setSummary = summaryState[1];
  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];
  var bandFilterState = useState([]); // multi-select: [] = all bands
  var bandFilter = bandFilterState[0]; var setBandFilter = bandFilterState[1];
  var modeFilterState = useState([]); // multi-select: [] = all modes
  var modeFilter = modeFilterState[0]; var setModeFilter = modeFilterState[1];
  var minSnrState = useState(-30); // -30 = no minimum (show all)
  var minSnr = minSnrState[0]; var setMinSnr = minSnrState[1];
  var autoRefreshState = useState(true);
  var autoRefresh = autoRefreshState[0]; var setAutoRefresh = autoRefreshState[1];
  var lastUpdateState = useState(null);
  var lastUpdate = lastUpdateState[0]; var setLastUpdate = lastUpdateState[1];
  var totalCachedState = useState(0);
  var totalCached = totalCachedState[0]; var setTotalCached = totalCachedState[1];
  var mapTabState = useState("map");
  var mapTab = mapTabState[0]; var setMapTab = mapTabState[1];

  var mapRef = useRef(null);
  var mapObjRef = useRef(null);
  var leafletReady = useState(false);
  var ready = leafletReady[0]; var setReady = leafletReady[1];

  var myPos = useMemo(function() { return gridToLatLon(myGrid); }, [myGrid]);

  // Toggle a value in a multi-select filter list. Returns a NEW array so React
  // re-runs the dependent memos/effects (band/mode filters below).
  var toggleFilter = function(list, val) {
    var idx = list.indexOf(val);
    if (idx >= 0) { var out = list.slice(); out.splice(idx, 1); return out; }
    return list.concat([val]);
  };

  var fetchData = useCallback(function() {
    setLoading(true);
    Promise.all([
      fetch(API + "/api/realtime/spots?limit=3000").then(function(r) { return r.json(); }),
      fetch(API + "/api/realtime/summary").then(function(r) { return r.json(); })
    ]).then(function(results) {
      var spotsData = results[0];
      var summaryData = results[1];
      setSpots(spotsData.spots || []);
      setTotalCached(spotsData.totalCached || 0);
      setLastUpdate(spotsData.lastFetch ? new Date(spotsData.lastFetch) : null);
      setSummary(summaryData.summary || []);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, []);

  // Initial fetch + auto refresh
  useEffect(function() {
    fetchData();
    var interval = null;
    if (autoRefresh) interval = setInterval(fetchData, 60000);
    return function() { if (interval) clearInterval(interval); };
  }, [autoRefresh, fetchData]);

  // Load Leaflet
  useEffect(function() {
    if (document.getElementById("lf-css")) { setReady(!!window.L); return; }
    var css = document.createElement("link");
    css.id = "lf-css"; css.rel = "stylesheet";
    css.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(css);
    var js = document.createElement("script");
    js.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    js.onload = function() { setReady(true); };
    document.head.appendChild(js);
  }, []);

  // Render map
  useEffect(function() {
    if (!ready || !mapRef.current || !window.L || mapTab !== "map") return;
    if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }

    var map = window.L.map(mapRef.current, { preferCanvas: true, zoomControl: true }).setView([20, 0], 2);
    mapObjRef.current = map;
    addDarkBasemap(map, { maxZoom: 18 });

    // My QTH marker
    if (myPos) {
      var myIcon = window.L.divIcon({
        html: '<div style="width:16px;height:16px;background:' + C.a3 + ';border:2px solid #fff;border-radius:50%;box-shadow:0 0 12px ' + C.a3 + '"></div>',
        className: "", iconSize: [16, 16], iconAnchor: [8, 8]
      });
      window.L.marker([myPos.lat, myPos.lon], { icon: myIcon }).addTo(map).bindPopup("<b>MY QTH</b><br/>" + myGrid);
    }

    // Plot spots as circle markers (sender positions)
    var plotted = 0;
    var step = spots.length > 5000 ? Math.ceil(spots.length / 5000) : 1;
    for (var i = 0; i < spots.length; i += step) {
      var s = spots[i];
      if (s.sl == null) continue;
      if (bandFilter.length && bandFilter.indexOf(s.b) < 0) continue;
      if (modeFilter.length && modeFilter.indexOf(s.m) < 0) continue;
      if (minSnr > -30 && s.snr < minSnr) continue;
      var color = BAND_COLORS[s.b] || "#666";
      window.L.circleMarker([s.sl, s.sn], {
        radius: 3, color: color, fillColor: color, fillOpacity: 0.7, weight: 0
      }).addTo(map).bindPopup(
        '<div style="font-family:monospace;font-size:12px">' +
        '<b style="color:' + C.a2 + '">' + s.sc + '</b><br/>' +
        s.b + ' ' + s.m + '<br/>' +
        'SNR: ' + s.snr + ' dB<br/>' +
        (s.d ? s.d.toLocaleString() + ' km' : '') +
        '</div>'
      );
      plotted++;
    }

    // Draw propagation lines for a subset (from sender to receiver)
    var lineStep = spots.length > 2000 ? Math.ceil(spots.length / 800) : Math.ceil(spots.length / 400);
    if (lineStep < 1) lineStep = 1;
    for (var j = 0; j < spots.length; j += lineStep) {
      var sp = spots[j];
      if (sp.sl == null || sp.rl == null) continue;
      if (bandFilter.length && bandFilter.indexOf(sp.b) < 0) continue;
      if (modeFilter.length && modeFilter.indexOf(sp.m) < 0) continue;
      if (minSnr > -30 && sp.snr < minSnr) continue;
      var lc = BAND_COLORS[sp.b] || "#666";
      window.L.polyline([[sp.sl, sp.sn], [sp.rl, sp.rn]], { color: lc, weight: 0.6, opacity: 0.2 }).addTo(map);
    }

    setTimeout(function() { map.invalidateSize(); }, 300);
    return function() { if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; } };
  }, [ready, spots, myPos, myGrid, bandFilter, modeFilter, minSnr, mapTab]);

  // Compute Best Band Now recommendation
  var recommendation = useMemo(function() {
    if (!summary || summary.length === 0) return null;
    var myLon = myPos ? myPos.lon : 0;
    var utcHour = new Date().getUTCHours();
    var localSolar = (utcHour + Math.round(myLon / 15) + 24) % 24;
    var isNight = localSolar < 6 || localSolar >= 18;

    // Normalize and score each band/mode
    var maxCount = 0; var maxGrids = 0; var maxDist = 0;
    for (var i = 0; i < summary.length; i++) {
      if (summary[i].count > maxCount) maxCount = summary[i].count;
      if (summary[i].uniqueGrids > maxGrids) maxGrids = summary[i].uniqueGrids;
      if (summary[i].avgDist > maxDist) maxDist = summary[i].avgDist;
    }
    if (maxCount === 0) return null;

    var scored = [];
    for (var j = 0; j < summary.length; j++) {
      var s = summary[j];
      var countScore = s.count / maxCount;
      var snrScore = Math.max(0, Math.min(1, (s.avgSnr + 30) / 50));
      var gridScore = maxGrids > 0 ? s.uniqueGrids / maxGrids : 0;
      var distScore = maxDist > 0 ? s.avgDist / maxDist : 0;

      // Time-of-day multiplier
      var nightBias = BAND_NIGHT_BIAS[s.band] || 0;
      var timeMultiplier = 1;
      if (isNight) timeMultiplier = 1 + nightBias * 0.3;
      else timeMultiplier = 1 - nightBias * 0.3;

      var score = (countScore * 0.35 + snrScore * 0.2 + gridScore * 0.25 + distScore * 0.2) * timeMultiplier;
      scored.push({
        band: s.band, mode: s.mode, score: Math.round(score * 100),
        count: s.count, avgSnr: s.avgSnr, avgDist: s.avgDist,
        maxDist: s.maxDist, uniqueGrids: s.uniqueGrids
      });
    }
    scored.sort(function(a, b) { return b.score - a.score; });
    return scored;
  }, [summary, myPos]);

  // Filtered spots for table
  var filteredSpots = useMemo(function() {
    var result = spots;
    if (bandFilter.length) result = result.filter(function(s) { return bandFilter.indexOf(s.b) >= 0; });
    if (modeFilter.length) result = result.filter(function(s) { return modeFilter.indexOf(s.m) >= 0; });
    // Apply SNR floor BEFORE the slice(-300) below, otherwise the row cap could
    // hide strong signals that the user explicitly asked to see.
    if (minSnr > -30) result = result.filter(function(s) { return s.snr >= minSnr; });
    // Enrich with new-one flags
    var enriched = result.slice(-300).reverse().map(function(s) {
      var dxcc = lookupDXCC(s.sc);
      var ent = dxcc ? dxcc.entity : "Unknown";
      var isNewDxcc = ent !== "Unknown" && !newOneData.entities.has(ent);
      var isNewBand = ent !== "Unknown" && s.b && !newOneData.bandEntity.has(s.b + "|" + ent);
      var isNewGrid = s.sg && s.sg.length >= 4 && !newOneData.grids.has(s.sg.substring(0, 4));
      return { sc: s.sc, sg: s.sg, rc: s.rc, rg: s.rg, f: s.f, b: s.b, m: s.m, snr: s.snr, ts: s.ts, d: s.d, sl: s.sl, sn: s.sn, rl: s.rl, rn: s.rn, _ent: ent, _newDxcc: isNewDxcc, _newBand: isNewBand && !isNewDxcc, _newGrid: isNewGrid };
    });
    if (showNewOnly) enriched = enriched.filter(function(s) { return s._newDxcc || s._newBand || s._newGrid; });
    return enriched;
  }, [spots, bandFilter, modeFilter, minSnr, newOneData, showNewOnly]);

  // Available modes from current data
  var availableModes = useMemo(function() {
    var modes = {};
    for (var i = 0; i < spots.length; i++) modes[spots[i].m] = true;
    return Object.keys(modes).sort();
  }, [spots]);

  // Available bands from current data
  var availableBands = useMemo(function() {
    var bands = {};
    for (var i = 0; i < spots.length; i++) bands[spots[i].b] = true;
    return BAND_ORDER.filter(function(b) { return bands[b]; });
  }, [spots]);

  // Band activity summary for bar chart
  var bandActivity = useMemo(function() {
    if (!summary || summary.length === 0) return [];
    var byBand = {};
    for (var i = 0; i < summary.length; i++) {
      var s = summary[i];
      if (!byBand[s.band]) byBand[s.band] = { band: s.band, count: 0, modes: [] };
      byBand[s.band].count += s.count;
      byBand[s.band].modes.push(s.mode + "(" + s.count + ")");
    }
    return BAND_ORDER.filter(function(b) { return byBand[b]; }).map(function(b) { return byBand[b]; });
  }, [summary]);

  var formatTime = function(ts) {
    if (!ts) return "-";
    var d = new Date(ts * 1000);
    return (d.getUTCHours() < 10 ? "0" : "") + d.getUTCHours() + ":" + (d.getUTCMinutes() < 10 ? "0" : "") + d.getUTCMinutes();
  };

  var btnStyle = function(active) {
    return {
      padding: "6px 14px", borderRadius: 6, border: "1px solid " + (active ? C.a1 : C.bd),
      background: active ? C.a1 + "20" : C.s2, color: active ? C.a1 : C.t2,
      cursor: "pointer", fontSize: 12, fontFamily: "'Courier New',monospace", fontWeight: active ? 700 : 400
    };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>
            {"\u{1F4E1}"} {t(lang, "realtimeTitle")}
          </h2>
          <div style={{ fontSize: 12, color: C.t3, marginTop: 4 }}>{t(lang, "realtimeDesc")}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={function() { setAutoRefresh(!autoRefresh); }} style={btnStyle(autoRefresh)}>
            {autoRefresh ? "\u25CF" : "\u25CB"} {t(lang, "autoRefresh")}
          </button>
          <button onClick={fetchData} style={btnStyle(false)}>
            {"\u{1F504}"} {t(lang, "refreshNow")}
          </button>
        </div>
      </div>

      {/* HERO - BEST BAND RIGHT NOW */}
      {recommendation && recommendation.length > 0 && (
        <Card glow={C.a2} style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ background: "linear-gradient(135deg, " + C.a2 + "18, " + C.a2 + "05)", padding: "24px 28px" }}>
            {/* Main recommendation - big and centered */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: C.a2, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>
                {"\u{1F4E1}"} {t(lang, "bestBandNow")}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: BAND_COLORS[recommendation[0].band] || "#666",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18, fontWeight: 900, color: "#000", fontFamily: "'Courier New',monospace",
                  boxShadow: "0 0 20px " + (BAND_COLORS[recommendation[0].band] || "#666") + "60"
                }}>{recommendation[0].band.replace("m","")}</div>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 900, color: C.t1, fontFamily: "'Courier New',monospace", lineHeight: 1 }}>
                    {recommendation[0].band}
                  </div>
                  <div style={{ fontSize: 16, color: C.a2, fontWeight: 700 }}>{recommendation[0].mode}</div>
                </div>
                <div style={{ textAlign: "left", marginLeft: 8 }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: C.a2, fontFamily: "'Courier New',monospace" }}>{recommendation[0].score}<span style={{ fontSize: 14, color: C.t3 }}>/100</span></div>
                  <div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: 1 }}>{t(lang, "propagationScore")}</div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 14, fontSize: 12, color: C.t2 }}>
                <span>{t(lang, "spotCount")}: <b style={{ color: C.t1 }}>{recommendation[0].count.toLocaleString()}</b></span>
                <span>{t(lang, "avgSnr")}: <b style={{ color: recommendation[0].avgSnr > -10 ? C.a2 : C.a5 }}>{recommendation[0].avgSnr} dB</b></span>
                <span>{t(lang, "avgDistRt")}: <b style={{ color: C.t1 }}>{recommendation[0].avgDist.toLocaleString()} km</b></span>
                <span>{t(lang, "activeGrids")}: <b style={{ color: C.t1 }}>{recommendation[0].uniqueGrids}</b></span>
              </div>
            </div>

            {/* Other top bands - compact row */}
            {recommendation.length > 1 && (
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                {recommendation.slice(1, 5).map(function(r, idx) {
                  return (
                    <div key={r.band + r.mode} style={{
                      background: C.s1, border: "1px solid " + C.bd, borderRadius: 8,
                      padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, minWidth: 180
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%",
                        background: BAND_COLORS[r.band] || "#666",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 800, color: "#000", fontFamily: "'Courier New',monospace"
                      }}>{r.band.replace("m","")}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: "'Courier New',monospace" }}>
                          {r.band} {r.mode}
                        </div>
                        <div style={{ fontSize: 10, color: C.t3 }}>
                          Score: <span style={{ color: C.a1, fontWeight: 700 }}>{r.score}</span> &middot; {r.count} spots &middot; {r.avgSnr}dB
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* KPI ROW */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <StatBox icon={"\u{1F4E1}"} label={t(lang, "activeSpots")} value={spots.length.toLocaleString()} color={C.a1} />
        <StatBox icon={"\u{1F4FB}"} label={t(lang, "activeBands")} value={availableBands.length} color={C.a2} />
        <StatBox icon={"\u{1F4E6}"} label={t(lang, "totalCached")} value={totalCached.toLocaleString()} color={C.a4} />
        <StatBox icon={"\u{1F552}"} label={t(lang, "lastUpdateRt")} value={lastUpdate ? lastUpdate.toLocaleTimeString() : "-"} color={C.a6} sub={lastUpdate ? lastUpdate.toLocaleDateString() : ""} />
      </div>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: C.t3, marginRight: 6 }}>{t(lang, "filterBand")}:</span>
        <button onClick={function() { setBandFilter([]); }} style={btnStyle(!bandFilter.length)}>
          {t(lang, "allBands")}
        </button>
        {availableBands.map(function(b) {
          var on = bandFilter.indexOf(b) >= 0;
          return (
            <button key={b} onClick={function() { setBandFilter(toggleFilter(bandFilter, b)); }}
              style={Object.assign({}, btnStyle(on), { borderColor: BAND_COLORS[b] || C.bd, color: on ? BAND_COLORS[b] : C.t2 })}>
              {b}
            </button>
          );
        })}
        <span style={{ fontSize: 12, color: C.t3, marginLeft: 12, marginRight: 6 }}>{t(lang, "filterMode")}:</span>
        <button onClick={function() { setModeFilter([]); }} style={btnStyle(!modeFilter.length)}>
          {t(lang, "allModes")}
        </button>
        {availableModes.map(function(m) {
          var on = modeFilter.indexOf(m) >= 0;
          return (
            <button key={m} onClick={function() { setModeFilter(toggleFilter(modeFilter, m)); }} style={btnStyle(on)}>
              {m}
            </button>
          );
        })}
        <span style={{ fontSize: 12, color: C.t3, marginLeft: 12, marginRight: 6 }}>{t(lang, "minSnr")}:</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="range" min={-30} max={30} step={1} value={minSnr}
            onChange={function(e) { setMinSnr(parseInt(e.target.value, 10)); }}
            style={{ accentColor: C.a1, width: 110, cursor: "pointer" }} />
          <span style={{ fontSize: 12, fontWeight: 700, minWidth: 62, color: minSnr > -30 ? C.a1 : C.t3, fontFamily: "'Courier New',monospace" }}>
            {minSnr > -30 ? ("≥ " + minSnr + " dB") : t(lang, "allSnr")}
          </span>
        </div>
        {userQsos.length > 0 && (
          <label style={{ marginLeft: 16, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 12, color: showNewOnly ? C.a5 : C.t3 }}>
            <input type="checkbox" checked={showNewOnly} onChange={function(e) { setShowNewOnly(e.target.checked); }} style={{ accentColor: C.a5 }} />
            {"\u{1F525}"} {t(lang, "showNewOnly")}
          </label>
        )}
      </div>

      {/* BAND ACTIVITY BAR */}
      {bandActivity.length > 0 && (
        <Card style={{ padding: 18 }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>
            {"\u{1F4FB}"} {t(lang, "bandActivity")} <span style={{ fontWeight: 400, color: C.t3, fontSize: 11 }}>({t(lang, "spotsLast15")})</span>
          </h3>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 120, padding: "0 4px" }}>
            {bandActivity.map(function(ba) {
              var maxC = bandActivity.reduce(function(m, x) { return x.count > m ? x.count : m; }, 1);
              var h = Math.max(8, Math.round((ba.count / maxC) * 100));
              return (
                <div key={ba.band} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ fontSize: 10, color: C.t2, fontWeight: 600 }}>{ba.count}</div>
                  <div style={{
                    width: "100%", height: h, borderRadius: "4px 4px 0 0",
                    background: "linear-gradient(180deg, " + (BAND_COLORS[ba.band] || "#666") + ", " + (BAND_COLORS[ba.band] || "#666") + "60)",
                    minWidth: 20, transition: "height 0.3s"
                  }}></div>
                  <div style={{ fontSize: 10, color: C.t3, fontWeight: 600 }}>{ba.band}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* MAP */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid " + C.bd, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>
            {"\u{1F5FA}\uFE0F"} {t(lang, "realtimeMap")}
          </h3>
          {/* Band color legend */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {availableBands.slice(0, 8).map(function(b) {
              return (
                <div key={b} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: C.t3 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: BAND_COLORS[b] }}></div>
                  {b}
                </div>
              );
            })}
          </div>
        </div>
        {loading && spots.length === 0 ? (
          <div style={{ height: 450, display: "flex", alignItems: "center", justifyContent: "center", color: C.t3 }}>
            {t(lang, "noSpotsYet")}
          </div>
        ) : (
          <div ref={mapRef} style={{ width: "100%", height: 450, background: C.bg }}></div>
        )}
      </Card>

      {/* SPOTS TABLE */}
      <Card style={{ padding: 18 }}>
        <h3 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>
          {"\u{1F4CB}"} {t(lang, "spotsTable")} <span style={{ fontWeight: 400, color: C.t3, fontSize: 11 }}>({filteredSpots.length} spots)</span>
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'Courier New',monospace" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid " + C.bd }}>
                {[t(lang,"timeLabel"), t(lang,"sender"), "DXCC", "Band", "Mode", t(lang,"snrLabel"), t(lang,"distLabel"), "Grid", "New"].map(function(h) {
                  return <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: C.t3, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {filteredSpots.map(function(s, i) {
                var rowBg = s._newDxcc ? "#ef444415" : s._newBand ? "#f59e0b10" : "transparent";
                return (
                  <tr key={i} style={{ borderBottom: "1px solid " + C.s3, background: rowBg, transition: "background 0.2s" }}
                    onMouseEnter={function(e) { if (!s._newDxcc && !s._newBand) e.currentTarget.style.background = C.s2; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = s._newDxcc ? "#ef444415" : s._newBand ? "#f59e0b10" : "transparent"; }}>
                    <td style={{ padding: "6px", color: C.t2 }}>{formatTime(s.ts)}</td>
                    <td style={{ padding: "6px", color: s._newDxcc ? "#ef4444" : C.a2, fontWeight: 700 }}>{s.sc}</td>
                    <td style={{ padding: "6px", color: C.t3, fontSize: 10 }}>{s._ent !== "Unknown" ? s._ent : "-"}</td>
                    <td style={{ padding: "6px" }}>
                      <span style={{ color: BAND_COLORS[s.b] || C.t1, fontWeight: 600 }}>{s.b}</span>
                    </td>
                    <td style={{ padding: "6px", color: C.a4 }}>{s.m}</td>
                    <td style={{ padding: "6px", color: s.snr > -10 ? C.a2 : s.snr > -20 ? C.a6 : C.a5 }}>{s.snr} dB</td>
                    <td style={{ padding: "6px", color: C.t1 }}>{s.d ? s.d.toLocaleString() + " km" : "-"}</td>
                    <td style={{ padding: "6px", color: C.t3 }}>{s.sg || "-"}</td>
                    <td style={{ padding: "6px" }}>
                      {s._newDxcc && <span style={{ background: "#ef4444", color: "#fff", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{t(lang,"newDxcc")}</span>}
                      {s._newBand && <span style={{ background: "#f59e0b", color: "#000", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{t(lang,"newBandSlot")}</span>}
                      {s._newGrid && !s._newDxcc && !s._newBand && <span style={{ background: C.a1 + "40", color: C.a1, padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{t(lang,"newGrid")}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredSpots.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: 30, color: C.t3 }}>{t(lang, "noSpotsYet")}</div>
        )}
      </Card>
    </div>
  );
}
